import React from "react";
import { connect } from "react-redux";
import { Input, Select } from "../../Input";
import { Button } from "../../Button";
import language from "../../../language";
import axios from "axios";
import toastMessage from "../../../utils/toastMessage";
import hospitalQuery from "../../../utils/queries/hospitalQuery";
import accountQuery from "../../../utils/queries/accountQuery";
import icons from "../../../constants/icons";
import { getStorage } from "../../../utils/storage";
import { fetchPatients } from "../../../utils/queries/patient";
import moment from "moment";
import bloodGroups from "../../../constants/bloodGroups";

const API_URL = process.env.REACT_APP_BBMS_BASE_API;

const EXAM_UNITS = {
  HCT: "%",
  Hgb: "g/dL",
  "PLT Count": "×10³/μL",
};

const ALL_EXAMS = ["ABO/RH", "HCT", "Hgb", "PLT Count"];

class NewPatient extends React.Component {
  state = {
    requesterName: "",
    requesterPosition: "",
    error: {},
    isSubmitting: false,
    hospitals: [],
    result: "",
    accounts: [],
    date: moment().format("YYYY-MM-DD HH:mm"),
    patients: [],
    user: {},
    // multi-add mode: keyed by exam name
    examResults: {
      "ABO/RH": "",
      HCT: "",
      Hgb: "",
      "PLT Count": "",
    },
  
    exam: null,
    hospital: null,
    patient: null,
};

  componentDidMount = async () => {
    const user = await getStorage();
    this.setState({ user });

    const currentHospitalId =
      this.props.hospitalId || user?.hospital?._id || user?.hospital;
    const currentHospitalName =
      this.props.hospitalName ||
      user?.hospital?.name ||
      user?.hospitalName ||
      this.props?.hospital?.name;

    const hospitals = await this.getHospitals(
      true,
      currentHospitalId ? { id: currentHospitalId } : null,
    );
    this.getAccounts(true);

    if (this.props._id) {
      this.setState({
        ...this.props,
        date: moment(this.props.date).format("YYYY-MM-DD HH:mm"),
        hospital: this.props.hospital
          ? {
              label: this.props.hospital.name,
              value: this.props.hospital._id,
            }
          : null,
        patient: this.props.patient
          ? {
              label:
                this.props.patient.firstName +
                " " +
                this.props.patient.lastName,
              value: this.props.patient._id,
            }
          : null,
        addedBy: this.props.addedBy
          ? {
              label: this.props.addedBy.name,
              value: this.props.addedBy._id,
            }
          : null,
        exam: this.props.exam
          ? {
              label: this.props.exam,
              value: this.props.exam,
            }
          : null,

        result:
          this.props.exam === "ABO/RH"
            ? {
                label: this.props.result,
                value: this.props.result,
              }
            : this.props.result,
      });
    } else {
      if (currentHospitalId) {
        const selectedHospital = (hospitals || []).find(
          (el) => el.value === currentHospitalId,
        );
        this.setState({
          hospital:
            selectedHospital ||
            ({
              label: currentHospitalName || currentHospitalId,
              value: currentHospitalId,
            }),
        });
      }

      if (this.props.patientId) {
        const patientLabel =
          this.props.patientName ||
          this.props.patientBBMSNumber ||
          this.props.patientId;
        this.setState({
          patient: { label: patientLabel, value: this.props.patientId },
        });
      }
    }
  };

  getHospitals = async (isFetchingHospitals, search = {}) => {
    try {
      this.setState({ isFetchingHospitals });

      let data = await hospitalQuery(this.props.defaultLanguage, {
        type: "dropdown",
        page: 0,
        limit: 50,
        ...search,
      });

      if (!Array.isArray(data) || data.length === 0) {
        const user = this.state.user || (await getStorage());
        const fallbackHospitals = [];

        if (Array.isArray(user?.hospitals)) {
          user.hospitals.forEach((h) => {
            const id = h?._id || h?.value;
            const name = h?.name || h?.label;
            if (id && name) fallbackHospitals.push({ label: name, value: id });
          });
        }

        if (
          fallbackHospitals.length === 0 &&
          (user?.hospital?._id || user?.hospital)
        ) {
          fallbackHospitals.push({
            label: user?.hospital?.name || user?.hospitalName || "Hospital",
            value: user?.hospital?._id || user?.hospital,
          });
        }

        data = fallbackHospitals;
      }

      this.setState({ hospitals: data, isFetchingHospitals: false, page: 1, limit: 50 });
      return data;
    } catch (error) {
      this.setState({ isFetchingHospitals: false });
    }
  };

  getPatients = async (isFetchingPatient, search = {}) => {
    try {
      this.setState({ isFetchingPatient });

      const { patientBBMSNumber } = this.props;
      const data = await fetchPatients(this.props.defaultLanguage, {
        type: "dropdown",
        page: 0,
        limit: 50,
        ...search,
        patientBBMSNumber,
      });

      this.setState({ patients: data, isFetchingPatient: false });
      return data;
    } catch (error) {
      this.setState({ isFetchingPatient: false });
    }
  };

  getAccounts = async (isFetchingAccount, search = {}) => {
    try {
      this.setState({ isFetchingAccount });

      const data = await accountQuery(this.props.defaultLanguage, {
        type: "dropdown",
        page: 0,
        limit: 50,
        ...search,
      });

      this.setState({ accounts: data, isFetchingAccount: false });
      return data;
    } catch (error) {
      this.setState({ isFetchingAccount: false });
    }
  };

  onChangeText = async (field, e) => {
    let { error } = this.state;
    let inputValue = e && e?.target ? e?.target?.value : e;

    delete error[field];
    delete error["errorMessage"];

    const extraState = field === "exam" ? { result: "" } : {};
    await this.setState({ [field]: inputValue, error, ...extraState });
  };

  onChangeExamResult = (examName, value) => {
    this.setState((prev) => {
      const newError = { ...prev.error };
      delete newError.examResults;
      delete newError[`examResult_${examName}`];
      return {
        examResults: { ...prev.examResults, [examName]: value },
        error: newError,
      };
    });
  };

  // ── Edit mode: validate single exam/result ──────────────────────────────────
  validateForm() {
    let { hospital, result, exam, error, patient } = this.state;

    if (!hospital || hospital === "") {
      error.hospital = language[this.props.defaultLanguage].hospital_required;
    }
    if (!patient || patient === "") {
      error.patient = language[this.props.defaultLanguage].patient_required;
    }
    if (!exam) {
      error.exam = language[this.props.defaultLanguage].exam_required;
    }
    if (result === "" || !result) {
      error.result = language[this.props.defaultLanguage].result_required;
    } else if (
      exam?.value !== "ABO/RH" &&
      (isNaN(parseFloat(result)) || !/^-?\d*\.?\d+$/.test(String(result).trim()))
    ) {
      error.result = language[this.props.defaultLanguage].result_required;
    }

    this.setState({ error });
  }

  // ── Add mode: validate multi-exam ───────────────────────────────────────────
  validateMultiForm() {
    let { hospital, patient, examResults } = this.state;
    let error = {};

    if (!hospital || hospital === "") {
      error.hospital = language[this.props.defaultLanguage].hospital_required;
    }
    if (!patient || patient === "") {
      error.patient = language[this.props.defaultLanguage].patient_required;
    }

    const hasAtLeastOne = ALL_EXAMS.some((exam) => {
      const val = examResults[exam];
      return val !== "" && val !== null && val !== undefined;
    });

    if (!hasAtLeastOne) {
      error.examResults =
        language[this.props.defaultLanguage].result_required ||
        "Enter at least one exam result";
    }

    // Validate numeric exams
    ALL_EXAMS.filter((e) => e !== "ABO/RH").forEach((exam) => {
      const val = examResults[exam];
      if (val !== "" && val !== null && val !== undefined) {
        if (isNaN(parseFloat(val)) || !/^-?\d*\.?\d+$/.test(String(val).trim())) {
          error[`examResult_${exam}`] =
            language[this.props.defaultLanguage].result_required ||
            "Invalid value";
        }
      }
    });

    this.setState({ error });
  }

  // ── Edit mode submit ─────────────────────────────────────────────────────────
  onSubmit = async () => {
    await this.validateForm();

    if (Object.keys(this.state.error).length === 0) {
      this.setState({ isSubmitting: true });

      let { hospital, patient, exam, result } = this.state,
        url = `${API_URL}/patient-lab-result`,
        { _id } = this.props,
        method = "POST",
        user = await getStorage();

      let requestedBody = {
        patient: patient.value,
        hospital: hospital.value,
        exam: exam.value,
        result: result.value || result,
        addedBy: user._id || user.id,
      };

      if (_id && _id !== "") {
        method = "PUT";
        requestedBody.id = _id;
      }

      const options = {
        method,
        url,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + user.token,
        },
        data: requestedBody,
      };

      axios(options)
        .then(() => {
          this.setState({ isSubmitting: false });
          toastMessage(
            "success",
            language[this.props.defaultLanguage][
              method === "PUT" ? "update_data_success" : "add_data_success"
            ],
          );
          this.props.getData(true);
          this.props.handleCloseModal();
        })
        .catch((error) => {
          toastMessage("error", error);
          this.setState({ isSubmitting: false });
        });
    }
  };

  // ── Add mode submit: post one record per filled exam ────────────────────────
  onSubmitMulti = async () => {
    await this.validateMultiForm();

    if (Object.keys(this.state.error).length > 0) return;

    this.setState({ isSubmitting: true });

    const { hospital, patient, examResults } = this.state;
    const user = await getStorage();
    const url = `${API_URL}/patient-lab-result`;

    const entries = ALL_EXAMS.filter((exam) => {
      const val = examResults[exam];
      return val !== "" && val !== null && val !== undefined;
    });

    try {
      for (const exam of entries) {
        const rawVal = examResults[exam];
        const result = exam === "ABO/RH" ? rawVal?.value || rawVal : rawVal;

        await axios({
          method: "POST",
          url,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + user.token,
          },
          data: {
            patient: patient.value,
            hospital: hospital.value,
            exam,
            result,
            addedBy: user._id || user.id,
          },
        });
      }

      this.setState({ isSubmitting: false });
      toastMessage(
        "success",
        language[this.props.defaultLanguage].add_data_success,
      );
      this.props.getData(true);
      this.props.handleCloseModal();
    } catch (error) {
      toastMessage("error", error);
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const isEdit = !!this.props._id;
    const { examResults, error } = this.state;

    return (
      <div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-12">
              <Select
                loadOptions={(e) => this.getHospitals(true, { q: e })}
                leftIcon={icons.hospital}
                placeholder={language[this.props.defaultLanguage].select}
                label={language[this.props.defaultLanguage].hospital}
                required
                value={this.state.hospital}
                onChange={(e) => this.onChangeText("hospital", e)}
                error={this.state.error.hospital}
                isLoading={this.state.isFetchingHospitals}
                isDisabled={!!this.props.patientId}
              />
            </div>
            <div className="col-md-12">
              <Select
                loadOptions={(e) => this.getPatients(true, { q: e })}
                leftIcon={icons.user}
                placeholder={language[this.props.defaultLanguage].select}
                label={language[this.props.defaultLanguage].patient}
                required
                value={this.state.patient}
                onChange={(e) => this.onChangeText("patient", e)}
                error={this.state.error.patient}
                isLoading={this.state.isFetchingPatient}
                isDisabled={!!this.props.patientId}
              />
            </div>

            {isEdit ? (
              // ── Edit mode: single exam/result ──────────────────────────────
              <>
                <div className="col-md-12">
                  <Select
                    options={ALL_EXAMS.map((e) => ({ label: e, value: e }))}
                    leftIcon={icons.exam}
                    placeholder={language[this.props.defaultLanguage].exam_placeholder}
                    label={language[this.props.defaultLanguage].exam}
                    required
                    value={this.state.exam}
                    onChange={(e) => this.onChangeText("exam", e)}
                    error={this.state.error.exam}
                  />
                </div>
                <div className="col-md-12">
                  {this.state?.exam?.value === "ABO/RH" ? (
                    <Select
                      options={bloodGroups}
                      leftIcon={icons.result}
                      placeholder={language[this.props.defaultLanguage].select}
                      label={language[this.props.defaultLanguage].result}
                      required
                      value={this.state.result}
                      onChange={(e) => this.onChangeText("result", e)}
                      error={this.state.error.result}
                    />
                  ) : (
                    <Input
                      leftIcon={icons.result}
                      placeholder={language[this.props.defaultLanguage].result_placeholder}
                      label={language[this.props.defaultLanguage].result}
                      required
                      value={this.state.result}
                      onChange={(e) => this.onChangeText("result", e)}
                      error={this.state.error.result}
                      unit={EXAM_UNITS[this.state.exam?.value]}
                      type="text"
                      inputMode="decimal"
                    />
                  )}
                </div>
              </>
            ) : (
              // ── Add mode: all exams at once ────────────────────────────────
              <div className="col-md-12" style={{ marginTop: "0.5rem" }}>
                {error.examResults && (
                  <div style={{ color: "var(--danger-color)", fontSize: "var(--font-size-sm)", marginBottom: "0.5rem" }}>
                    {error.examResults}
                  </div>
                )}
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th style={{ width: "40%" }}>
                        {language[this.props.defaultLanguage].exam}
                      </th>
                      <th>
                        {language[this.props.defaultLanguage].result}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ verticalAlign: "middle", fontWeight: 500 }}>ABO/RH</td>
                      <td>
                        <Select
                          options={bloodGroups}
                          placeholder={language[this.props.defaultLanguage].select}
                          value={examResults["ABO/RH"]}
                          onChange={(e) => this.onChangeExamResult("ABO/RH", e)}
                          isClearable
                        />
                      </td>
                    </tr>
                    {["HCT", "Hgb", "PLT Count"].map((exam) => (
                      <tr key={exam}>
                        <td style={{ verticalAlign: "middle", fontWeight: 500 }}>
                          {exam}
                          {EXAM_UNITS[exam] && (
                            <span style={{ fontSize: "var(--font-size-sm)", color: "var(--text-color-60)", marginLeft: "4px" }}>
                              ({EXAM_UNITS[exam]})
                            </span>
                          )}
                        </td>
                        <td>
                          <Input
                            placeholder={language[this.props.defaultLanguage].result_placeholder}
                            value={examResults[exam]}
                            onChange={(e) => this.onChangeExamResult(exam, e?.target?.value)}
                            error={error[`examResult_${exam}`]}
                            type="text"
                            inputMode="decimal"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <Button
            text={language[this.props.defaultLanguage].cancel}
            onPress={this.props.handleCloseModal}
            className="btn-default"
          />
          <Button
            text={language[this.props.defaultLanguage].submit}
            onPress={isEdit ? this.onSubmit.bind(this) : this.onSubmitMulti.bind(this)}
            isSubmitting={this.state.isSubmitting}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(NewPatient);
